import * as Dialog from '@radix-ui/react-dialog'
import type { ReactNode } from 'react'
import { useTranslation } from '../localization/useTranslation'
import { usePortalContainer } from '../providers/portal'
import { IconX } from '../ui/icons'
import { scrollWidgetToTop } from '../util/scroll'
import { OverlayReporter } from './OverlayReporter'

/**
 * The shared "inspect & confirm" modal (mockup `.match-modal`), used for both a visual-search
 * product match and an add-by-category tile — the details are all props, so there's one dialog, not
 * two near-identical ones. Built on Radix Dialog for focus trapping, Escape/backdrop close, scroll
 * lock and ARIA. `Dialog.Portal container={…}` targets our shadow-root overlay node (not
 * document.body) so it's styled by our adopted stylesheet and isolated from the host storefront.
 */
export interface DetailDialogProps {
  trigger: ReactNode
  /** Hero content — a product image carousel, or a category icon. */
  hero: ReactNode
  /** `true` for a category icon so it's contained (not a full-bleed cover photo). */
  heroContain?: boolean
  /** Optional pill above the title (e.g. the "strong match" badge). */
  badge?: ReactNode
  /** Optional eyebrow line (category label / section). */
  eyebrow?: string
  title: string
  /** Optional line under the title (e.g. colour). */
  subtitle?: string
  rewardLabel: string
  /** Already-formatted reward amount. */
  rewardValue: string
  backLabel: string
  confirmLabel: string
  onConfirm: () => void
}

export function DetailDialog({
  trigger,
  hero,
  heroContain,
  badge,
  eyebrow,
  title,
  subtitle,
  rewardLabel,
  rewardValue,
  backLabel,
  confirmLabel,
  onConfirm,
}: DetailDialogProps) {
  const container = usePortalContainer()
  const { t } = useTranslation()

  return (
    // modal={false} so Radix's scroll-lock doesn't block the mouse wheel in our shadow root (see TermsDialog).
    <Dialog.Root modal={false}>
      <Dialog.Trigger asChild>{trigger}</Dialog.Trigger>
      <Dialog.Portal container={container ?? undefined}>
        {/* Plain scrim, not Dialog.Overlay: Radix renders Overlay as null when modal={false}. As a
            direct Portal child it's still open-state gated, and clicking it closes the dialog. */}
        <div className="modal-scrim" aria-hidden />
        <Dialog.Content className="modal match-modal" aria-describedby={undefined}>
          <OverlayReporter />
          <Dialog.Close className="icon-btn match-close" aria-label={t('terms.close')}>
            <IconX />
          </Dialog.Close>

          <div className={`match-hero${heroContain ? ' cat-hero' : ''}`}>{hero}</div>

          <div className="match-info">
            <div className="match-info-main">
              {badge}
              {eyebrow && <div className="mcat">{eyebrow}</div>}
              <Dialog.Title asChild>
                <h2>{title}</h2>
              </Dialog.Title>
              {subtitle && <div className="mcolor">{subtitle}</div>}
            </div>
            <div className="mreward">
              <span className="k">{rewardLabel}</span>
              <span className="v num">{rewardValue}</span>
            </div>
          </div>

          <div className="modal-foot">
            <Dialog.Close asChild>
              <button type="button" className="btn btn-ghost">
                {backLabel}
              </button>
            </Dialog.Close>
            <Dialog.Close asChild>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  onConfirm()
                  // The view swaps (dialog closes, finder resets / navigates) — bring the flow back
                  // into view (no-op if the customer hasn't scrolled down past it; see scrollWidgetToTop).
                  scrollWidgetToTop()
                }}
              >
                {confirmLabel}
              </button>
            </Dialog.Close>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
